/**
 * Commercial Summary — board-level view of the AI estate.
 *
 * Wave 29 — Bundle-independent. Consumes `/api/dashboard` via
 * `DashboardDataProvider`. Tenant-aware narrative selected by tenant id
 * still drives the prose; every figure is derived from the aggregate
 * REST payload (kpis, vendor + agent rollups, 12-month trend).
 */
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Banknote,
  Coins,
  Gauge,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { DashboardDataProvider, useDashboardData } from "@/contexts/DashboardDataContext";
import {
  SpendValueChart,
  CeoDepartmentsPanel,
} from "@/pages/dashboard/components/CeoPanels";
import { PanelCard, fmtAud } from "@/pages/dashboard/components/DashboardPrimitives";
import { fmtPer1k } from "@/pages/agents/components/AgentPrimitives";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { TrendChip } from "@/components/data/TrendChip";
import { cn } from "@/lib/utils";

// Stable colour palette for the vendor split.
const VENDOR_COLORS = ["#137DC5", "#0E8762", "#A87C2A", "#5B3FB0", "#B5651D", "#475569"];

// Short, tenant-specific commercial commentary.
const COMMENTARY = {
  "acme-bank": {
    headline: "Banking estate · operating at ROI 3.1× · top concern: vendor concentration",
    bullets: [
      "Run-rate AI spend stable inside the FY26 envelope (A$7.3M ARR vs A$8.0M budget). 8.6% headroom to year-end.",
      "Three governed agents (AML, Contact Centre, Vulnerable Customer) drive 49% of monthly burn — APRA CPS 230 critical-operations flagging needs CRO sign-off.",
      "OpenAI Enterprise + AWS Bedrock = 56% of vendor spend. Procurement evaluating Anthropic Enterprise direct as a Bedrock alternative for the FY27 renewal.",
      "A$430k in addressable savings identified (5.9% of ARR) — Contact Centre Copilot model swap to gpt-4o-mini is the single biggest lever (~A$129k/mo).",
    ],
  },
  "northwind-insurance": {
    headline: "Insurance estate · operating at ROI 3.0× · top concern: high-risk agents on premium models",
    bullets: [
      "Run-rate AI spend A$2.2M ARR (A$182k/mo) — 1.4% of digital budget, well inside benchmark for AU general insurance peers.",
      "Customer Service Copilot + Claims Triage + Claims Fraud Indicator drive 49% of monthly burn — all three are high-risk by design, so model substitution requires Legal + Underwriting review.",
      "OpenAI + Anthropic = 53% of vendor spend. Single-vendor risk on OpenAI flagged for the FY26 renewal pack.",
      "EU AI Act in scope for the Dublin underwriting subsidiary only — board confirmed scope at the Feb Risk Sub-Committee.",
    ],
  },
};

const EMPTY_COMMENTARY = {
  headline: "Connect your first vendor + import an agent to populate this view.",
  bullets: [
    "Once data flows in, this page becomes the board-level commercial narrative — spend, value, vendor concentration and ROI by department.",
    "All figures auto-derive from the AI estate; no manual report assembly required.",
  ],
};

const fmtPct = (n) => `${(n * 100).toFixed(0)}%`;
const fmtMultiple = (n) => `${n.toFixed(1)}×`;

export default function CommercialSummary() {
  return (
    <DashboardDataProvider>
      <CommercialSummaryInner />
    </DashboardDataProvider>
  );
}

function CommercialSummaryInner() {
  const navigate = useNavigate();
  const {
    tenant,
    kpis = {},
    trend12m = [],
    vendorBurnRollup = [],
    topBurners = [],
    benchmark,
    costOpportunities = [],
    isEmpty,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useDashboardData();

  // Annualised view (×12).
  const monthlySpend = kpis.monthlySpendAud || 0;
  const monthlyValue = kpis.monthlyValueAud || 0;
  const annualSpend = monthlySpend * 12;
  const annualValue = monthlyValue * 12;
  const roi = kpis.roiRatio || 0;
  const netGain = annualValue - annualSpend;

  const savingsRunway = useMemo(
    () => costOpportunities.reduce((s, o) => s + (o.estSavingsAud || 0), 0),
    [costOpportunities],
  );

  const budgetPctMetric = benchmark?.metrics?.find((m) => m.metric === "AI spend % of digital budget");

  const vendors = vendorBurnRollup.filter((v) => v.spendAud > 0);
  const totalSpend = vendors.reduce((s, v) => s + v.spendAud, 0) || 1;
  const top3Share = vendors.slice(0, 3).reduce((s, v) => s + v.spendAud, 0) / totalSpend;

  const commentary = isEmpty
    ? EMPTY_COMMENTARY
    : COMMENTARY[tenant?.id] || EMPTY_COMMENTARY;

  if (loading && !tenant) return <CommercialSkeleton />;
  if (error) return <CommercialError message={error.message} />;

  return (
    <div className="px-6 py-8 max-w-[1400px] mx-auto" data-testid="commercial-summary">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="text-meta uppercase tracking-[0.12em] text-ink-secondary">
            Commercial · Board summary
          </p>
          <h1 className="text-display-md sm:text-display-lg text-ink-primary mt-1">
            AI Spend · AI Value · ROI
          </h1>
          <p className="text-ink-secondary mt-2 max-w-2xl">
            One-page commercial narrative for {tenant?.name || "your AI estate"} — every figure
            derived from the agent inventory and vendor register; ready to drop into the next
            board pack.
          </p>
          <SyncedIndicator
            lastSyncedAt={lastSyncedAt}
            loading={loading}
            onRefetch={refetch}
            testId="commercial-summary-synced"
          />
        </div>
        <button
          type="button"
          onClick={() => navigate("/reports/builder")}
          className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700 transition-colors"
          data-testid="commercial-build-report-cta"
        >
          Build signed report <ArrowRight size={14} />
        </button>
      </div>

      {/* Hero KPIs */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <BigKpi
          label="Annualised AI spend"
          value={fmtAud(annualSpend)}
          sub={`${fmtAud(monthlySpend)} / month`}
          icon={Banknote}
          tone="neutral"
          testId="commercial-kpi-spend"
        />
        <BigKpi
          label="Annualised AI value"
          value={fmtAud(annualValue)}
          sub={`${fmtAud(monthlyValue)} / month`}
          icon={Coins}
          tone="positive"
          testId="commercial-kpi-value"
        />
        <BigKpi
          label="Net ROI multiple"
          value={fmtMultiple(roi)}
          sub={`${fmtAud(netGain)} net / yr`}
          icon={TrendingUp}
          tone="brand"
          testId="commercial-kpi-roi"
        />
        <BigKpi
          label="% of digital budget"
          value={budgetPctMetric ? `${budgetPctMetric.ours.toFixed(1)}%` : "—"}
          sub={budgetPctMetric ? `peer median ${budgetPctMetric.p50.toFixed(1)}%` : "—"}
          icon={Gauge}
          tone="neutral"
          testId="commercial-kpi-budget"
        />
      </div>

      {/* Narrative */}
      <div className="mt-6 surface-card shadow-e1 p-4" data-testid="commercial-narrative">
        <div className="flex items-start gap-2">
          <Sparkles size={16} className="text-gold-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-ink-primary">{commentary.headline}</p>
            <ul className="mt-2 space-y-1.5">
              {commentary.bullets.map((b) => (
                <li key={b} className="text-sm text-ink-secondary flex gap-2">
                  <span className="text-ink-disabled mt-0.5">·</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Spend vs Value chart */}
      <div className="mt-4">
        <SpendValueChart />
      </div>

      {/* Vendor split + Department ROI */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <CeoDepartmentsPanel />
        </div>
        <PanelCard
          title="Vendor concentration"
          testId="commercial-vendors-panel"
          footer={`Top 3 vendors = ${fmtPct(top3Share)} of spend · ${
            top3Share <= 0.7 ? "diversification healthy" : "concentration watch"
          }`}
        >
          <div className="space-y-3">
            <div className="flex h-3 w-full rounded overflow-hidden border border-border-subtle">
              {vendors.slice(0, 6).map((v, i) => {
                const pct = (v.spendAud / totalSpend) * 100;
                return (
                  <div
                    key={v.id}
                    style={{ width: `${pct}%`, background: VENDOR_COLORS[i % VENDOR_COLORS.length] }}
                    title={`${v.name} · ${pct.toFixed(1)}%`}
                    data-testid={`commercial-vendor-bar-${v.id}`}
                  />
                );
              })}
            </div>
            <ul className="space-y-2 text-sm">
              {vendors.slice(0, 6).map((v, i) => {
                const pct = ((v.spendAud / totalSpend) * 100).toFixed(1);
                return (
                  <li key={v.id} className="flex items-center gap-2" data-testid={`commercial-vendor-row-${v.id}`}>
                    <span
                      className="h-2 w-2 rounded-sm shrink-0"
                      style={{ background: VENDOR_COLORS[i % VENDOR_COLORS.length] }}
                    />
                    <span className="flex-1 min-w-0 truncate text-ink-primary">{v.name}</span>
                    <span className="text-meta text-ink-secondary tabular-nums shrink-0 w-12 text-right">{pct}%</span>
                    <span className="text-meta text-ink-disabled font-mono tabular-nums shrink-0 w-16 text-right">
                      {fmtPer1k(v.weightedPer1kToken)}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="text-[10px] text-ink-disabled uppercase tracking-[0.06em] pt-1 border-t border-border-subtle">
              Right column = burn-weighted $/1k tokens
            </p>
          </div>
        </PanelCard>
      </div>

      {/* Top burners + Savings runway */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <PanelCard
          title="Top 5 burners (this month)"
          testId="commercial-top-burners"
          footer={`Combined ${fmtAud(topBurners.reduce((s, a) => s + (a.monthlyCostAud || 0), 0))} / mo`}
          className="lg:col-span-2"
        >
          <ul className="divide-y divide-border-subtle/60">
            <li
              className="grid grid-cols-12 gap-3 items-center -mx-2 px-2 pb-2 text-[10px] font-bold tracking-[0.08em] uppercase text-ink-secondary"
              data-testid="commercial-top-burners-header"
            >
              <div className="col-span-7">Agent</div>
              <div className="col-span-2 text-right">Burn / mo</div>
              <div className="col-span-2 text-right">Value / mo</div>
              <div className="col-span-1 text-right">ROI</div>
            </li>
            {topBurners.map((a) => (
              <li
                key={a.id}
                className="grid grid-cols-12 gap-3 items-center py-2 cursor-pointer hover:bg-inset/40 -mx-2 px-2 rounded transition-colors"
                onClick={() => navigate(`/govern/agents/${a.id}`)}
                data-testid={`commercial-burner-${a.id}`}
              >
                <div className="col-span-7 min-w-0">
                  <p className="text-sm font-semibold text-ink-primary truncate">{a.name}</p>
                  <p className="text-meta text-ink-secondary truncate">
                    <span className="font-mono">{a.id}</span> · {a.businessUnit || a.department}
                  </p>
                </div>
                <div className="col-span-2 text-right">
                  <div className="inline-flex flex-col items-end gap-0.5">
                    <p className="text-sm font-semibold text-ink-primary tabular-nums">{fmtAud(a.monthlyCostAud)}</p>
                    <TrendChip id={a.id} size="xs" />
                  </div>
                </div>
                <div className="col-span-2 text-right">
                  <p className="text-sm text-risk-low font-semibold tabular-nums">{fmtAud(a.monthlyValueAud)}</p>
                </div>
                <div className="col-span-1 text-right">
                  <p className="text-sm font-semibold text-tralenex-700 tabular-nums">
                    {fmtMultiple(a.roiMultiple || 0)}
                  </p>
                </div>
              </li>
            ))}
            {topBurners.length === 0 && (
              <li className="py-6 text-center text-ink-secondary text-sm">No governed agents yet.</li>
            )}
          </ul>
        </PanelCard>

        <PanelCard
          title="Savings runway"
          testId="commercial-savings"
          footer={costOpportunities.length ? `${costOpportunities.length} opportunities tracked` : "No opportunities identified yet"}
        >
          <div className="text-center py-2">
            <p className="text-meta text-ink-secondary uppercase tracking-[0.08em]">Identified · annualised</p>
            <p className="text-display-md text-ink-primary tabular-nums mt-1" data-testid="commercial-savings-total">
              {fmtAud(savingsRunway)}
            </p>
            <p className="text-meta text-ink-disabled mt-1">
              {annualSpend > 0 ? `${((savingsRunway / annualSpend) * 100).toFixed(1)}% of current spend` : "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/measure/cost")}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md bg-inset hover:bg-inset/70 text-sm font-semibold text-ink-primary transition-colors"
            data-testid="commercial-savings-cta"
          >
            Open cost optimisation <ArrowRight size={14} />
          </button>
        </PanelCard>
      </div>

      {/* Footer — governance posture link */}
      <div className="mt-4 surface-card shadow-e1 p-4 flex items-center gap-3" data-testid="commercial-footer">
        <ShieldCheck size={18} className="text-tralenex-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink-primary">Audit chain · every figure is hash-linked</p>
          <p className="text-meta text-ink-secondary">
            Numbers on this page are derived from the agent inventory and vendor register at render time.
            Sign &amp; export a tamper-evident snapshot from the report builder.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/reports/builder")}
          className="text-meta font-semibold text-tralenex-700 hover:text-tralenex-900 inline-flex items-center gap-1 shrink-0"
          data-testid="commercial-build-cta"
        >
          Build pack <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

function CommercialSkeleton() {
  return (
    <div className="px-6 py-8 max-w-[1400px] mx-auto" data-testid="commercial-summary-loading">
      <div className="h-8 w-64 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[300px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function CommercialError({ message }) {
  return (
    <div className="px-6 py-8" data-testid="commercial-summary-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="text-meta uppercase tracking-[0.12em] text-risk-high">Commercial · Board summary</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load the commercial summary</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function BigKpi({ label, value, sub, icon: Icon, tone, testId }) {
  const toneCls = {
    neutral: "text-ink-primary",
    positive: "text-risk-low",
    brand: "text-tralenex-700",
  }[tone || "neutral"];
  return (
    <div className="surface-card shadow-e1 p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-meta text-ink-secondary uppercase tracking-[0.08em]">{label}</p>
        <Icon size={14} className="text-ink-disabled" />
      </div>
      <p className={cn("text-display-md tabular-nums mt-1.5", toneCls)}>{value}</p>
      <p className="text-meta text-ink-secondary tabular-nums mt-1">{sub}</p>
    </div>
  );
}
